import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowUpRight, Hammer, Search, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import AnimatedValue from "@/components/AnimatedValue";

const STATUS_PILL = {
  development: "bg-[var(--peak-blue)] text-white border-[var(--peak-blue)]",
  on_hold:     "bg-amber-100 text-amber-900 border-amber-800",
  delivered:   "bg-emerald-100 text-emerald-900 border-emerald-800",
  cancelled:   "bg-neutral-200 text-neutral-700 border-neutral-700",
};

const money = (n) => (n ? `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "—");

export default function DevDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [seeding, setSeeding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/development/projects");
      setRows(data || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = rows.filter((r) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (r.project_name || "").toLowerCase().includes(q)
      || (r.client_name || "").toLowerCase().includes(q);
  });

  const stats = {
    total: rows.length,
    active: rows.filter((r) => r.status === "development").length,
    delivered: rows.filter((r) => r.status === "delivered").length,
    pipeline_value: rows
      .filter((r) => r.status !== "cancelled")
      .reduce((sum, r) => sum + (Number(r.value) || 0), 0),
  };

  const seedDemos = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/development/seed-demo");
      if (data.seeded) {
        toast.success(`Seeded ${data.seeded} demo project${data.seeded === 1 ? "" : "s"}`);
      } else {
        toast.info(data.message || "Demo projects already seeded");
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail || e.message));
    } finally {
      setSeeding(false);
    }
  };

  if (!user?.is_superadmin) {
    return (
      <div className="p-10" data-testid="dev-dashboard-forbidden">
        <div className="border-2 border-[var(--peak-ink)] p-8 bg-white max-w-lg">
          <div className="font-heading text-2xl font-black mb-2">Superadmin only</div>
          <div className="text-sm text-[var(--peak-muted)]">The Development Dashboard is scoped to platform superadmins.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="dev-dashboard-page">
      <div className="border-b-2 border-[var(--peak-ink)] bg-white">
        <div className="px-8 py-6 flex items-end justify-between gap-6">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--peak-accent)]">Superadmin · Delivery</div>
            <h1 className="font-heading text-4xl font-black tracking-tighter mt-1 flex items-center gap-3">
              <Hammer className="w-8 h-8" strokeWidth={2.5} />
              Development Dashboard
            </h1>
            <div className="text-sm text-[var(--peak-muted)] mt-1">
              Projects currently in build. Auto-created when a signed PropGen deal reaches "Won".
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--peak-muted)] pointer-events-none" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search project or client…"
                data-testid="dev-search-input"
                className="pl-9 h-10 w-72 border-2 border-[var(--peak-ink)] font-mono text-xs"
              />
            </div>
            <Button
              onClick={seedDemos}
              disabled={seeding}
              data-testid="dev-seed-demos-btn"
              className="h-10 bg-[var(--peak-ink)] hover:bg-[var(--peak-accent)] text-white font-heading font-bold uppercase text-xs tracking-wide"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {seeding ? "Seeding…" : "Seed demo projects"}
            </Button>
          </div>
        </div>
        <div className="px-8 pb-6 grid grid-cols-2 sm:grid-cols-4 border-t-2 border-[var(--peak-ink)] bg-[var(--peak-bg)]">
          {[
            { label: "Total", value: stats.total, testid: "stat-total" },
            { label: "In Development", value: stats.active, testid: "stat-active" },
            { label: "Delivered", value: stats.delivered, testid: "stat-delivered" },
            { label: "Pipeline Value", value: money(stats.pipeline_value), testid: "stat-value" },
          ].map((s, i) => (
            <div key={s.label} className={`py-5 px-6 ${i < 3 ? "border-r-2 border-[var(--peak-ink)]" : ""}`} data-testid={s.testid}>
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--peak-muted)]">{s.label}</div>
              <div className="font-heading text-4xl font-black tracking-tighter mt-1"><AnimatedValue value={s.value} /></div>
            </div>
          ))}
        </div>
      </div>

      <div className="p-8">
        <div className="border-2 border-[var(--peak-ink)] bg-white" data-testid="dev-projects-table">
          <div className="grid grid-cols-[2fr_1.4fr_1fr_1fr_1fr_60px] items-center px-4 py-2 border-b-2 border-[var(--peak-ink)] bg-[var(--peak-bg)] text-[10px] uppercase tracking-wider font-mono text-[var(--peak-muted)]">
            <div>Project</div>
            <div>Client</div>
            <div>Current Phase</div>
            <div>Assigned</div>
            <div>Progress</div>
            <div></div>
          </div>

          {loading ? (
            <div className="p-8 text-sm text-[var(--peak-muted)]" data-testid="dev-loading">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-16 flex flex-col items-center text-center gap-3" data-testid="dev-empty">
              <div className="w-14 h-14 border-2 border-dashed border-[var(--peak-ink)]/40 flex items-center justify-center">
                <Hammer className="w-6 h-6 text-[var(--peak-muted)]" />
              </div>
              <div className="font-heading text-lg font-bold">No projects in development yet</div>
              <div className="text-sm text-[var(--peak-muted)] max-w-md">
                A project lands here automatically when a PropGen deal reaches the "Won" stage in the pipeline.
                Or seed a few demo projects to see how it looks.
              </div>
              <Button
                onClick={seedDemos}
                disabled={seeding}
                data-testid="dev-empty-seed-btn"
                className="mt-2 bg-[var(--peak-accent)] hover:bg-[var(--peak-ink)] text-white font-heading font-bold uppercase text-xs tracking-wide"
              >
                {seeding ? "Seeding…" : "Seed demo projects →"}
              </Button>
            </div>
          ) : (
            filtered.map((r) => (
              <button
                key={r.id}
                onClick={() => nav(`/superadmin/development/${r.id}`)}
                data-testid={`dev-project-row-${r.id}`}
                className="peak-row w-full grid grid-cols-[2fr_1.4fr_1fr_1fr_1fr_60px] items-center px-4 py-3 border-b border-[var(--peak-border)] last:border-b-0 text-left"
              >
                <div>
                  <div className="font-semibold text-sm truncate">{r.project_name}</div>
                  <div className="text-[11px] font-mono text-[var(--peak-muted)] uppercase truncate mt-0.5">
                    <span className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider border ${STATUS_PILL[r.status] || STATUS_PILL.development}`}>
                      {r.status.replace("_", " ")}
                    </span>
                    <span className="ml-2">· {money(r.value)}</span>
                  </div>
                </div>
                <div className="text-sm text-[var(--peak-muted)] truncate">{r.client_name}</div>
                <div className="text-xs text-[var(--peak-ink)] truncate">{r.current_phase || "—"}</div>
                <div className="text-xs text-[var(--peak-muted)] truncate">{r.assigned_admin_name || "Unassigned"}</div>
                <div>
                  <div className="w-full h-2 bg-[var(--peak-border)] border border-[var(--peak-ink)]">
                    <div
                      className="h-full bg-[var(--peak-accent)]"
                      style={{ width: `${Math.min(100, r.progress || 0)}%` }}
                    />
                  </div>
                  <div className="text-[10px] font-mono text-[var(--peak-muted)] mt-1">{r.progress || 0}%</div>
                </div>
                <div className="flex justify-end"><ArrowUpRight className="w-4 h-4 text-[var(--peak-muted)]" /></div>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
